import {PLAYER_LAYOUT, SUIT, GAME_MODE, PLAYER_ACTION} from '@constant/game';
import dayjs from 'dayjs';
import {GameLogicInterface, GameRoomInterface} from 'poker-interfaces';
import {JoinedRoomProps} from 'poker-interfaces/game-room/game-room-interface';
import {AutoAction, CardObj} from 'poker-interfaces/gameLogic/gameLogicInterface';

export type DateRange = {
  from: Date;
  to: Date;
};

export type PokerCard = {
  suit: SUIT;
  rank: number;
};

export type GameResultOnScreen = GameLogicInterface.Result;

export interface GameRoomComponent {
  tableIndex: number; // TODO: reference, this or roomNumber
  tableInfo: JoinedRoomProps;
  encryptionKey: string;
  isTableActive: boolean;
  isGameOverlayShown: boolean;

  directGameInviteHandler?: Function;
  buyInAgainHandler?: Function;
  roomInitHandler?: Function;
  gameInitHandler?: Function;
  playerInActionHandler?: Function;
  playerFoldHandler?: Function;
  playerDecisionHandler?: Function;
  tableUpdateHandler?: Function;
  gameRestartHandler?: Function;
  gameResultHandler?: Function;
  stickerHandler?: Function;
  privateInfoHandler?: Function;
  yourTurnHandler?: Function;
  insuranceHandler?: Function;
  tournamentPlayerRankingHandler?: Function;
  tournamentResultAnnouncementHandler?: Function;
  tournamentRebuyInviteHandler?: Function;
  tournamentStackHandler?: Function;

  gameRoomComponentOnCopy?: Function;
  gameRoomComponentOnShare?: Function;
  gameRoomComponentOnClickEmoji?: Function;
  gameRoomComponentOnClickHistory?: Function;
  gameRoomComponentOnClickWithdrawExtraBet?: Function;
  gameRoomComponentOnClickAddExtraBet?: Function;
  gameRoomComponentOnAddExtraBetSuccess?: Function;
  gameRoomComponentOnAfkNotification?: Function;
  gameRoomComponentOnExitRoomNotification?: Function;
  gameRoomComponentOnRemaining?: Function;
  gameRoomComponentOnExpired?: Function;
  gameRoomComponentOnBuyInResp?: Function;
  gameRoomComponentOnTopupResp?: Function;
  gameRoomComponentOnWithdrawBetResp?: Function;
  gameRoomComponentOnSwitchToObserverResp?: Function;
  gameRoomComponentOnExtendPrivateRoomResp?: Function;

  gameRoomComponentOnSetActionResult?: Function;
  gameRoomComponentOnSitInResult?: Function;
  gameRoomComponentOnSitOutResult?: Function;

  indicateTableInAction?: (tableIndex: number) => void;

  quitTable?: () => Promise<any>;
  sitOut?: () => Promise<any>;
  sitOutPopUp?: (data) => Promise<any>;
  sitOutKick?: () => void;
  sitIn?: () => Promise<any>;
  disconnectTable?: () => Promise<any>;
  reconnect?: () => void;
  buyInExpiredHandler?: () => void;

  unlockAudio?: () => void;

  setIsWithdrawChipsBtnShown?: Function;
  setIsDepositChipsBtnShown?: Function;
  setIsEmojiBtnShown?: Function;
  isGameRoomMuted: boolean;
  onAudioError: () => void;
  dismissTimerModals?: () => void;

  joinRoomErrorHandler?: (data: any) => void;
}

export interface TableArea {
  tableIndex: number;
  isCurrentTableInView: boolean;
  shouldCardsAnimationBePlayed: boolean;
  communityHands: PokerComponent[];
  outCards: PokerComponent[];
  players: PlayerComponent[];
  playerInActionOrder: number;
  pot?: number;
  prevPot?: number;
  gameResult?: GameLogicInterface.Result;
  gameResultOnScreen: GameResultOnScreen;
  gameMode: GameLogicInterface.GameType;
  isAllIn: boolean;
  roomCode: string;
  waitingTime: number;
  actionTime: number;
  buyInCallback?: (position: number) => void;
  moveChipsToPlayers?: () => void;
  areActionsDisabled: boolean;
  insuranceInfo: GameLogicInterface.Insurance;
  dealerButtonPositionFromGameRoom?: number;
  myHands?: PokerComponent[];
  myBestCombination?: BestCombination;
  roomType: GameRoomInterface.RoomType;
}

export interface AbsolutePosition {
  top?: number;
  left?: number;
  bottom?: number;
  right?: number;
  height?: number;
  width?: number;

  xCenter?: number;
  yCenter?: number;

  // Prevent incompatibility
  x?: number;
  y?: number;
}

export interface PlayerComponent extends GameLogicInterface.Player {
  // useImperative methods
  moveChipToPot?: () => void;
  // getAbsolutePosition?: () => AbsolutePosition;
  // getPlayerIconCenterPosition?: Function;
  containerElement?: HTMLElement;
  outCards: PokerComponent[];

  originalIndex?: number; // For observer mode

  layout?: PLAYER_LAYOUT;
  potPosition?: AbsolutePosition;
  actionTime: number;
  isResultShowing: boolean;
  isTimerHidden: boolean;
  isWinner?: boolean;
  isInAction?: boolean;
  winPercentage?: number;
  gameMode: GameLogicInterface.GameType;
  isAllInMode?: boolean;
  isAllIn: boolean;
  roomCode: string;
  noOfPlayers: number;
  tableElement: HTMLElement;

  // For chip pos calculation
  sequenceOrder?: number;
  isCurrentTableInView?: boolean;
  /**
   * @override
   */
  hand: PokerComponent[];
  myHands?: PokerComponent[];
  myBestCombination?: BestCombination;
  autoAction?: AutoAction;
  resetTimer?: ({expiredAt, duration}: {expiredAt: dayjs.Dayjs; duration: number}) => void;
  hideTimer?: () => void;
  unhideTimer?: () => void;
  distributeCards?: () => void;
}

export interface MyPlayerComponent extends GameLogicInterface.Player {
  // useImperative methods
  moveChipToPot?: () => void;
  // getAbsolutePosition?: () => AbsolutePosition;
  containerElement?: HTMLElement;
  outCards: PokerComponent[];

  layout?: PLAYER_LAYOUT;
  potPosition?: AbsolutePosition;
  actionTime: number;
  isResultShowing: boolean;
  isTimerHidden: boolean;
  isWinner?: boolean;
  isInAction?: boolean;
  winPercentage?: number;
  gameMode: GameLogicInterface.GameType;
  roomCode: string;
  bestCombination: BestCombination;

  /**
   * @override
   */
  hand: PokerComponent[];
}
export interface SubtitleInterface {
  className?: string;
  text: string;
}

export interface BalanceRowInterface {
  icon: JSX.Element;
  name: JSX.Element | string;
  amountColor: string;
  prefix: string;
  amount: number;
  haveButton?: Boolean;
}

export type TransactionType =
  | 'win'
  | 'lose'
  | 'win-lose'
  | 'player-deposit'
  | 'insurance'
  | 'private-room'
  | 'gift'
  | 'first-login'
  | 'redeem-demo'
  | 'lucky-draw'
  | 'purchase-item'
  | 'purchase-diamond'
  | 'private-room-rebate';

export type AnimationName = 'ALL_IN' | 'CALL';

export interface BestCombination {
  cards?: Array<GameLogicInterface.CardObj>;
  combinationName: GameLogicInterface.Combination | 'ONE_PAIR';
  displayValue: GameLogicInterface.Result['cardValues'];
}

export type Currency = 'coin' | 'demo-coin' | 'diamond';

export type GameStepsAction = 'WIN' | PLAYER_ACTION;

export interface PokerComponent extends GameLogicInterface.CardObj {
  className?: any;
  cardClassName?: string;
  cardSize?: 'extraSmall' | 'small' | 'medium' | 'square' | 'large' | number;
  isInitiallyFlipped?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  cardHeight?: number;
  hasNoGoldBorder?: boolean;
  state?: 'DARKEN' | 'RAISED' | 'NORMAL';
  suit;
  rank;
  cardIndex?: number;
  shouldCardAnimationBePlayed?: boolean;
  isCommunityCard?: boolean;
  flipDelayFromParent?: number;
}

export interface PokerInsuranceComponent extends GameLogicInterface.CardObj {
  className?: any;
  cardClassName?: string;
  cardSize?: 'extraSmall' | 'small' | 'medium' | 'square';
  isFlipped?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  cardHeight?: number;
  hasNoGoldBorder?: boolean;
  state?: 'DARKEN' | 'RAISED' | 'NORMAL';
  suit;
  rank;
}

export interface PokerOutcardComponent extends GameLogicInterface.CardObj {
  className?: any;
  cardClassName?: string;
  cardSize?: 'extraSmall' | 'small' | 'medium' | 'square';
  isInitiallyFlipped?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  cardHeight?: number;
  hasNoGoldBorder?: boolean;
  state?: 'DARKEN' | 'RAISED' | 'NORMAL';
  suit;
  rank;
}

export interface EmojiIdentifier {
  image: string;
  diamondItemId?: number;
  animationFile?: string;
}

export interface EmojiStatus extends EmojiIdentifier {
  playerId: number;
  targetPlayerId: number;
  roomCode: string;
  timestamp?: number;
}

export interface EmojiContextInterface {
  onClickEmoji: (param: EmojiIdentifier) => void;
  OnCancelShootEmoji: () => void;
  shootEmoji: (playerId: number, targetPlayerId: number) => void;
  emojiStatus: EmojiStatus;
  resetEmojiStatus: () => void;
  toggleStickerDrawerVisibility: (value: boolean) => void;
  setSenderId: (senderId: number) => void;
  setReceiverId: (receiverId: number) => void;
  emojiReceiverId: number;
  emojiSenderId: number;
  isEmojiSending: boolean;
  setIsEmojiSendingByContext: (value: boolean) => void;
  // send emoji button is hidden/disabled when the player is sit-out
  isEmojiButtonShown: boolean;
}

export type ComponentsPositions = {
  [componentId: string]: {top: number; right: number; bottom: number; left: number};
};

export interface PositionContextInterface {
  componentsPositions: ComponentsPositions;
  updateComponentPosition: (
    componentId: string,
    newTop: number,
    newRight: number,
    newBottom: number,
    newLeft: number
  ) => void;
  scaleUpValue: number;
  updateScaleUpValue: (newValue: number) => void;
  playerIconCenterPositions: any[];
  updatePlayerIconCenterPositions: (array: any[]) => void;
  updatePlayerChipPlacementPosition: (index: number, position: AbsolutePosition) => void;
  playerChipPlacementPosition: AbsolutePosition[];
}

export interface ResizeLoadingContextInterface {
  setIsResizeLoading: (state: boolean) => void;
}

export interface TabBubbleHandVisibleContextInterface {
  isMyHandVisibleInBubble?: boolean;
  setIsMyHandVisibleInBubble: (value: boolean) => void;
}

export type Notification = 'NOTIFY_AFK_LEAVE_TABLE' | 'NOTIFY_FORCE_EXIT_ROOM';

export type PlayerInAction = {
  roomCode: string;
  playerOrder: number;
};

export interface TabBubble {
  hasAlert?: boolean;
  isMyTurn: boolean;
  expireAt: string;
  actionDuration: number;
  pot?: number;
  hands?: CardObj[];
  isSatOut?: boolean;
  isTournament?: boolean;
  displayMode: 'POT' | 'HANDS' | 'SIT_OUT';
}

export interface TabBubbleUiInterface {
  index: number;
  bubbleData: TabBubble;
  isHighlighted: boolean;
  roomCode: string;
  onBlur?: () => void;
  onClick?: () => void;
  onHover?: () => void;
}
